import { useEffect, useMemo, useRef, useState } from "react";
import { C } from "../lib/theme";
import { Icon } from "./Icon";
import { supabase } from "../lib/supabase";
import { gradientForId } from "../lib/gradients";
import { fmtK } from "../lib/money";

// Global search modal — opens on Cmd/Ctrl+K or the TopNav Search button.
// Queries products (title ILIKE) and sellers (store_name ILIKE) in parallel,
// debounced at 200ms. Empty state shows recent searches from localStorage.
const HISTORY_KEY = "kapio_search_history";
const MAX_HISTORY = 6;

function loadHistory() {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.slice(0, MAX_HISTORY) : [];
  } catch {
    return [];
  }
}

function pushHistory(q) {
  if (!q.trim()) return;
  const current = loadHistory();
  const next = [q, ...current.filter((h) => h.toLowerCase() !== q.toLowerCase())].slice(0, MAX_HISTORY);
  try {
    localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
  } catch {
    /* ignore quota / privacy-mode errors */
  }
}

function firstImage(images) {
  if (!Array.isArray(images) || !images.length) return null;
  const img = images[0];
  if (typeof img === "string") return img;
  return img?.thumb || img?.med || img?.full || null;
}

export function SearchModal({ open, onClose, onOpenProduct, onVisitStore }) {
  const [q, setQ] = useState("");
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState(() => loadHistory());
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setQ("");
    setProducts([]);
    setStores([]);
    setHistory(loadHistory());
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 2) {
      setProducts([]);
      setStores([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      const [{ data: prodData }, { data: storeData }] = await Promise.all([
        supabase
          .from("products")
          .select("id, title, price, compare_price, images, seller_id, sellers(store_name)")
          .eq("status", "active")
          .is("deleted_at", null)
          .ilike("title", `%${term}%`)
          .limit(8),
        supabase
          .from("sellers")
          .select("id, store_name, store_slug, category, address, rating_avg, rating_count, logo_url, product_count")
          .eq("status", "active")
          .ilike("store_name", `%${term}%`)
          .limit(5),
      ]);
      if (cancelled) return;
      setProducts(prodData || []);
      setStores(storeData || []);
      setLoading(false);
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [q, open]);

  const hasResults = products.length + stores.length > 0;
  const termReady = q.trim().length >= 2;

  function chooseProduct(p) {
    pushHistory(q);
    onOpenProduct?.(p);
    onClose();
  }

  function chooseStore(s) {
    pushHistory(q);
    onVisitStore?.(s);
    onClose();
  }

  if (!open) return null;

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(28,28,28,0.45)",
        backdropFilter: "blur(4px)",
        WebkitBackdropFilter: "blur(4px)",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        zIndex: 200,
        padding: "80px 24px 24px",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "100%",
          maxWidth: 640,
          background: C.white,
          borderRadius: 20,
          boxShadow: "0 24px 60px rgba(0,0,0,0.25)",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          maxHeight: "calc(100vh - 120px)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            padding: "14px 18px",
            borderBottom: "1px solid rgba(196,193,183,0.3)",
          }}
        >
          <Icon name="search" size={18} color={C.muted} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search products and stores…"
            style={{
              flex: 1,
              border: "none",
              outline: "none",
              background: "transparent",
              fontSize: 15,
              fontFamily: "inherit",
              color: C.charcoal,
            }}
          />
          <button
            onClick={onClose}
            style={{
              border: "1px solid rgba(196,193,183,0.4)",
              background: C.cream,
              color: C.muted,
              fontSize: 10.5,
              fontWeight: 700,
              padding: "3px 7px",
              borderRadius: 6,
              cursor: "pointer",
              letterSpacing: 0.5,
            }}
          >
            ESC
          </button>
        </div>

        <div style={{ overflowY: "auto", flex: 1 }}>
          {!termReady && history.length > 0 && (
            <ResultSection title="Recent">
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "6px 18px 14px" }}>
                {history.map((h) => (
                  <button
                    key={h}
                    onClick={() => setQ(h)}
                    style={{
                      padding: "5px 10px",
                      borderRadius: 999,
                      background: C.cream,
                      border: "1px solid rgba(196,193,183,0.4)",
                      fontSize: 12,
                      color: C.slate,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    {h}
                  </button>
                ))}
              </div>
            </ResultSection>
          )}

          {!termReady && history.length === 0 && (
            <div style={{ padding: "28px 18px 32px", textAlign: "center", color: C.muted, fontSize: 13 }}>
              Start typing to find products and stores on Kapio.
            </div>
          )}

          {termReady && loading && (
            <div style={{ padding: "18px", color: C.muted, fontSize: 12.5 }}>Searching…</div>
          )}

          {termReady && !loading && !hasResults && (
            <div style={{ padding: "28px 18px 32px", textAlign: "center" }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.charcoal }}>No matches for "{q.trim()}"</div>
              <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
                Try a different keyword or browse all stores.
              </div>
            </div>
          )}

          {termReady && !loading && stores.length > 0 && (
            <ResultSection title={`Stores · ${stores.length}`}>
              {stores.map((s) => (
                <StoreRow key={s.id} s={s} onClick={() => chooseStore(s)} />
              ))}
            </ResultSection>
          )}

          {termReady && !loading && products.length > 0 && (
            <ResultSection title={`Products · ${products.length}`}>
              {products.map((p) => (
                <ProductRow key={p.id} p={p} onClick={() => chooseProduct(p)} />
              ))}
            </ResultSection>
          )}
        </div>
      </div>
    </div>
  );
}

function ResultSection({ title, children }) {
  return (
    <div style={{ borderBottom: "1px solid rgba(196,193,183,0.2)" }}>
      <div
        style={{
          fontSize: 10.5,
          fontWeight: 800,
          color: C.muted,
          letterSpacing: 1,
          padding: "12px 18px 6px",
          textTransform: "uppercase",
        }}
      >
        {title}
      </div>
      <div>{children}</div>
    </div>
  );
}

function ProductRow({ p, onClick }) {
  const img = useMemo(() => firstImage(p.images), [p.images]);
  const [g1, g2] = gradientForId(p.id);
  return (
    <button
      onClick={onClick}
      style={{
        all: "unset",
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 18px",
        cursor: "pointer",
        width: "100%",
        boxSizing: "border-box",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = C.cream)}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: img ? `url(${img}) center/cover` : `linear-gradient(135deg, ${g1}, ${g2})`,
          flexShrink: 0,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: C.charcoal,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {p.title}
        </div>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
          {p.sellers?.store_name || "Store"}
        </div>
      </div>
      <div style={{ fontSize: 13, fontWeight: 900, color: C.hunter }}>{fmtK(p.price)}</div>
    </button>
  );
}

function StoreRow({ s, onClick }) {
  const [g1, g2] = gradientForId(s.id);
  return (
    <button
      onClick={onClick}
      style={{
        all: "unset",
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 18px",
        cursor: "pointer",
        width: "100%",
        boxSizing: "border-box",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = C.cream)}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 10,
          background: s.logo_url ? `url(${s.logo_url}) center/cover` : `linear-gradient(135deg, ${g1}, ${g2})`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: C.white,
          fontWeight: 900,
          flexShrink: 0,
          fontSize: 14,
        }}
      >
        {!s.logo_url && (s.store_name?.[0]?.toUpperCase() || "S")}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: C.charcoal }}>{s.store_name}</div>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
          {[s.category, s.address].filter(Boolean).join(" · ") || "Store on Kapio"}
        </div>
      </div>
      {s.rating_avg > 0 && (
        <div style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 12, fontWeight: 700, color: C.slate }}>
          <Icon name="star" size={12} color={C.amber} />
          {Number(s.rating_avg).toFixed(1)}
        </div>
      )}
    </button>
  );
}
